import React, { useCallback, useMemo } from "react"
import { Modal, Radio, Input, Button, Typography, Flex, Form } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import { PhoneInput, validatePhoneNumber } from "ui/Input/PhoneInput"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { Delete } from "ui/Icon/DSIcon/Delete"
import IconWithBg from "ui/IconWithBg"
import { useGuestFields } from "./hooks/useGuestFields"
import { useShareWaiverSubmission } from "./hooks/useShareWaiverSubmission"
import { getFormInitialValues } from "./utils/waiverHelpers"
import { useSelector } from "react-redux"

const { Text } = Typography

const ShareWaiver = ({uuid, visible, onClose, id }) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const guestData = useSelector((state) => state.digitalWaiver.waiverSubmissionsByTripBooking.guest_data)
  const nonPrimaryGuestCount = useMemo(
    () => (guestData || []).filter((g) => !g.is_primary_guest).length,
    [guestData]
  )
  
  const logoSrc = useMemo(() => 
    theme === themes.sportswoman ? bgWoman : bgMan, 
    [theme]
  )
  
  const {
    guestFields,
    handleAddGuest,
    handleGuestSendMethodChange,
    handleRemoveGuest,
    resetGuestFields
  } = useGuestFields()
  
  const { submitWaiver, loading } = useShareWaiverSubmission(
    id,uuid,
    () => {
      onClose()
      form.resetFields()
      resetGuestFields()
    },
    (error) => {
      console.error("Waiver submission error:", error)
    }
  )
  const handleClose = useCallback(() => {
    form.resetFields()
    resetGuestFields()
    onClose()
  }, [form, resetGuestFields, onClose])
  const handleAddGuestWithForm = useCallback(() => {
    const newGuestId = handleAddGuest()
    
    // Initialize form values for the new guest
    form.setFieldValue(["guests", newGuestId, "sendMethod"], "text")
    form.setFieldValue(["guests", newGuestId, "phones", 0], { countryCode: "US", fullNumber: "" })
    form.setFieldValue(["guests", newGuestId, "emails", 0], "")
  }, [form, handleAddGuest])

  const handleGuestSendMethodChangeWithForm = useCallback((guestId, method) => {
    handleGuestSendMethodChange(guestId, method)
    form.setFieldValue(["guests", guestId, "sendMethod"], method)
  }, [form, handleGuestSendMethodChange])

  const onFinish = useCallback((values) => {
    submitWaiver(values)
  }, [submitWaiver])

  const onValuesChange = useCallback((changedValues, allValues) => {
    // Handle any immediate validation or side effects here
  }, [])

  return (
    <Modal
      className={styles.modal}
      open={visible}
      onCancel={handleClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width={700}
      maskClosable={false}
      centered
    >
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        layout="vertical"
        initialValues={getFormInitialValues()}
      >
        <h3 className={`pb-xl ${styles.modalTitle}`}>{t("embedded.booking_confirmation.share_waiver")}</h3>
        {guestFields.map((guest) => (
          <div key={guest.id} className={styles.guestSection}>
            <div className={styles.sendMethodSection}>
              <Form.Item name={["guests", guest.id, "sendMethod"]} className={`mb-0 ${styles.radioGroupWrapper}`} initialValue="text">
                <Radio.Group
                  onChange={(e) => handleGuestSendMethodChangeWithForm(guest.id, e.target.value)}
                  value={guest.sendMethod}
                  className={styles.radioGroup}
                >
                  <Radio value="text" className={styles.radio}>
                    {t("embedded.booking_confirmation.send_by_text")}
                  </Radio>
                  <Radio value="email" className={styles.radio}>
                    {t("embedded.booking_confirmation.send_by_email")}
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {guest.sendMethod === "text" ? (
                <Form.List name={["guests", guest.id, "phones"]}>
                  {(fields, { add, remove }) => (
                    <div className={styles.phoneInputsContainer}>
                      {fields.map((field, index) => (
                        <div key={field.key} className={styles.phoneInputWrapper}>
                          <Flex gap={10} className="flex-1" align="center">
                            <Form.Item
                              className="flex-1"
                              {...field}
                              label={t("embedded.booking_confirmation.phone_number")}
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (!value?.fullNumber?.trim()) {
                                      return Promise.reject(t("form_validation.phone_required"))
                                    }
                                    return validatePhoneNumber(value.fullNumber)
                                  }
                                }
                              ]}
                            >
                              <PhoneInput
                                initialCountryCode={form.getFieldValue(["guests", guest.id, "phones", index, "countryCode"]) || "US"}
                                initialValue={form.getFieldValue(["guests", guest.id, "phones", index, "fullNumber"]) || ""}
                                onChange={(value) => {
                                  form.setFieldValue(["guests", guest.id, "phones", index], value)
                                }}
                                size="large"
                              />
                            </Form.Item>
                            {guestFields.length > 1 && (
                              <div
                                className="mt-s"
                                onClick={() => {
                                  handleRemoveGuest(guest.id)
                                  // Remove the guest's data from form
                                  const guests = form.getFieldValue("guests") || []
                                  form.setFieldsValue({
                                    guests: guests.filter((_, idx) => idx !== guest.id)
                                  })
                                }}
                              >
                                <IconWithBg className={styles.deleteIcon} height={18} width={18} icon={<Delete fill="#ff0000" />} />
                              </div>
                            )}
                          </Flex>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.List>
              ) : (
                <Form.List name={["guests", guest.id, "emails"]}>
                  {(fields, { add, remove }) => (
                    <div className={styles.phoneInputsContainer}>
                      {fields.map((field, index) => (
                        <div key={field.key} className={styles.phoneInputWrapper}>
                          <Flex gap={10} className={`${styles.emailInputWrapper} flex-1`} align="center">
                            <LabeledField
                              {...field}
                              labelKey={t("embedded.booking_confirmation.email_address")}
                              rules={[
                                { required: true, message: t("embedded.booking_confirmation.plz_enter_email_address") },
                                { type: "email", message: t("embedded.booking_confirmation.plz_enter_valid_email") }
                              ]}
                            >
                              <Input placeholder="Enter email address" className={`!mb-3xl ${styles.emailInput}`} size="large" />
                            </LabeledField>
                            {guestFields.length > 1 && (
                              <div
                                className="mt-xs"
                                onClick={() => {
                                  handleRemoveGuest(guest.id)
                                  // Remove the guest's data from form
                                  const guests = form.getFieldValue("guests") || []
                                  form.setFieldsValue({
                                    guests: guests.filter((_, idx) => idx !== guest.id)
                                  })
                                }}
                              >
                                <IconWithBg className={styles.deleteIcon} height={18} width={18} icon={<Delete fill="#ff0000" />} />
                              </div>
                            )}
                          </Flex>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.List>
              )}
            </div>
          </div>
        ))}

        <Button color="primary" onClick={handleAddGuestWithForm} variant="outlined" disabled={guestFields.length >= nonPrimaryGuestCount}>
          {t("embedded.booking_confirmation.add_guest")}
        </Button>

        <Flex justify="space-between" align="center" className="mt-4xl">
          <Flex gap={10} align="center">
            <Text>{t("settings.powered_by")} </Text>
            <img src={logoSrc} alt="Logo" />
          </Flex>
          <Flex gap={8}>
            <Button color="primary" variant="outlined" onClick={onClose}>
              {t("global.cancel")}
            </Button>
            <Button color='primary' variant='solid' htmlType="submit" loading={loading} disabled={loading}>
              {t("global.send")}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}

export default ShareWaiver
